import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  computed,
  effect,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CompoundFilterGroupDef } from './compound-filter-selection.model';

/**
 * Renders the "Search on…" checkbox tree inside the filter-option popup.
 *
 * Emits the new set of selected compound-filter keys whenever the user
 * checks or unchecks an option.  Enforces a minimum of one selected key
 * by reverting to `defaultSelectedKeys` when the last item is deselected.
 * That same default option is also locked (rendered disabled) whenever it
 * is the ONLY currently-selected option, so the user can see upfront why it
 * can't be unchecked instead of watching it snap back after the fact.
 */
@Component({
  selector: 'miq-compound-filter-selection',
  templateUrl: './compound-filter-selection.component.html',
  styleUrls: ['./compound-filter-selection.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatCheckboxModule, MatTooltipModule],
})
export class CompoundFilterSelectionComponent {
  readonly groups = input<CompoundFilterGroupDef[]>([]);
  readonly defaultSelectedKeys = input<number[]>([]);
  readonly searchOnLabel = input('Search on…');
  readonly selectAllLabel = input('Select All');
  readonly selectedKeys = input<number[] | undefined>(undefined);
  /**
   * Tooltip shown on a default option's checkbox while it's locked/disabled
   * (i.e. it's the only currently-selected option). Configurable by the host;
   * left empty for now (Angular Material shows no tooltip for an empty string).
   */
  readonly disabledTooltip = input('');

  readonly selectionChange = output<number[]>();

  readonly checkedKeys = signal(new Set<number>());

  constructor() {
    effect(() => {
      const keys = this.selectedKeys();
      const defaults = untracked(() => this.defaultSelectedKeys());
      this.checkedKeys.set(new Set(keys?.length ? keys : defaults));
    });
  }

  /**
   * Stop ALL click events from bubbling out of this component into the
   * mat-menu panel.  Angular Material's mat-menu closes itself when a click
   * event reaches the panel element (`(click)="closed.emit('click')"`), so we
   * must prevent that at the host element boundary.
   */
  @HostListener('click', ['$event'])
  onHostClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  // ── Convenience helpers ──────────────────────────────────────────────────

  private readonly allKeys = computed(() => this.groups().flatMap(g => g.items.map(i => i.key)));

  readonly isAllSelected = computed(() => this.allKeys().every(k => this.checkedKeys().has(k)));

  readonly isPartiallySelected = computed(() => {
    const count = this.allKeys().filter(k => this.checkedKeys().has(k)).length;
    return count > 0 && !this.isAllSelected();
  });

  readonly groupsAllSelected = computed(() => {
    const keys = this.checkedKeys();
    return new Set(this.groups().filter(g => g.items.every(i => keys.has(i.key))));
  });

  /**
   * True when every currently-checked key is one of the configured
   * `defaultSelectedKeys` — i.e. nothing beyond the default option(s) is
   * selected. This is the state in which a default option's checkbox must
   * lock (see `isItemDisabled`), since unchecking it would leave nothing
   * selected at all.
   */
  private readonly isOnlyDefaultSelected = computed(() => {
    const checked = this.checkedKeys();
    if (checked.size === 0) return true;
    const defaults = new Set(this.defaultSelectedKeys());
    return [...checked].every(key => defaults.has(key));
  });

  readonly viewGroups = computed(() => {
    const keys = this.checkedKeys();
    return this.groups().map(g => ({
      def: g,
      label: g.label,
      allSelected: g.items.every(i => keys.has(i.key)),
      items: g.items.map(i => ({ ...i, checked: keys.has(i.key), disabled: this.isItemDisabled(i.key) })),
    }));
  });

  isItemChecked(key: number): boolean {
    return this.checkedKeys().has(key);
  }

  /**
   * A default option's checkbox locks (renders disabled) when it is the ONLY
   * currently-selected option — the user can still freely check/uncheck it
   * while anything else is also selected. Non-default options are never
   * disabled by this rule.
   */
  isItemDisabled(key: number): boolean {
    return this.defaultSelectedKeys().includes(key) && this.isOnlyDefaultSelected();
  }

  isGroupAllSelected(group: CompoundFilterGroupDef): boolean {
    return group.items.every(i => this.checkedKeys().has(i.key));
  }

  isGroupPartiallySelected(group: CompoundFilterGroupDef): boolean {
    const count = group.items.filter(i => this.checkedKeys().has(i.key)).length;
    return count > 0 && count < group.items.length;
  }

  // ── Toggle handlers ──────────────────────────────────────────────────────

  toggleItem(checked: boolean, key: number): void {
    const next = new Set(this.checkedKeys());
    if (checked) {
      next.add(key);
    } else {
      next.delete(key);
    }
    this.commit(next);
  }

  toggleGroup(checked: boolean, group: CompoundFilterGroupDef): void {
    const next = new Set(this.checkedKeys());
    group.items.forEach(i => {
      if (checked) {
        next.add(i.key);
      } else {
        next.delete(i.key);
      }
    });
    this.commit(next);
  }

  toggleAll(checked: boolean): void {
    const next = new Set(this.checkedKeys());
    if (checked) {
      this.allKeys().forEach(k => next.add(k));
    } else {
      next.clear();
    }
    this.commit(next);
  }

  // ── Internal ─────────────────────────────────────────────────────────────

  private commit(next: Set<number>): void {
    // Guard: never allow an empty selection – revert to defaultSelectedKeys instead. The
    // default option's own checkbox additionally renders as disabled in this exact state
    // (see isItemDisabled), so in practice this fallback only guards non-default items being
    // deselected down to zero (e.g. a group/"Select All" bulk-uncheck) rather than being the
    // primary way users experience the lock.
    const finalized = next.size > 0 ? next : new Set(this.defaultSelectedKeys());
    this.checkedKeys.set(finalized);
    this.selectionChange.emit([...finalized]);
  }
}
